import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, CheckCircle2, Clock, Calendar } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#1E90FF", "#38BDF8", "#2563EB", "#7C3AED", "#A78BFA"];
const TOOLTIP_STYLE = { background: "#0B0F17", border: "1px solid rgba(30,144,255,0.3)", borderRadius: 8 };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/participants").then((r) => setRecent(r.data.slice(0, 5)));
  }, []);
  if (!stats) return <div className="text-slate-400">Carregando...</div>;
  const cards = [
    { label: "Total Participantes", v: stats.total, icon: Users },
    { label: "Confirmados", v: stats.confirmed, icon: CheckCircle2 },
    { label: "Pendentes", v: stats.pending, icon: Clock },
    { label: "Eventos", v: stats.events_total, icon: Calendar },
  ];
  return (
    <div className="space-y-8">
      <div>
        <div className="label">Painel</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">Dashboard</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="ci-card p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon size={20} className="text-blue-400" />
            <div className="font-display text-3xl font-black mt-3 tabular-nums">{c.v}</div>
            <div className="text-xs text-slate-400 mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      {stats.next_event && (
        <div className="ci-card p-6">
          <div className="label">Próximo Evento</div>
          <h3 className="font-display text-xl font-bold mt-2">{stats.next_event.edition_number}ª Edição — {stats.next_event.name}</h3>
          <p className="text-sm text-slate-400 mt-1">{new Date(stats.next_event.start_date).toLocaleDateString("pt-BR")} • {stats.next_event.location}</p>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="ci-card p-6">
          <div className="label mb-4">Inscrições por Edição</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={stats.by_event}>
                <XAxis dataKey="edition" tickFormatter={(v) => `#${v}`} stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#1E90FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="ci-card p-6">
          <div className="label mb-4">Por Faixa Etária</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={stats.by_age} dataKey="count" nameKey="bucket" outerRadius={80} label={(e) => `${e.bucket}: ${e.count}`}>
                  {stats.by_age.map((d) => <Cell key={d.bucket} fill={COLORS[stats.by_age.indexOf(d) % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="ci-card p-6">
        <div className="label mb-4">Inscrições Recentes</div>
        {recent.length === 0 ? (
          <div className="text-sm text-slate-400">Nenhuma inscrição ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-500 text-left"><th className="pb-2">ID</th><th className="pb-2">Nome</th><th className="pb-2">Evento</th><th className="pb-2">Data</th></tr></thead>
            <tbody>
              {recent.map((p) => (
                <tr key={p.id} className="border-t border-blue-500/10">
                  <td className="py-2 font-mono text-xs text-blue-300">{p.registration_id}</td>
                  <td className="py-2">{p.full_name}</td>
                  <td className="py-2 text-slate-400">#{p.event_edition}</td>
                  <td className="py-2 text-slate-400 text-xs">{new Date(p.created_at).toLocaleDateString("pt-BR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
